import React, { useState, useRef, useEffect } from 'react';
import {
  Home,
  Upload,
  Mic,
  Loader2,
  AlertCircle,
  CheckCircle,
  Settings,
  Play,
  Pause,
  X,
  FileAudio,
  Clock,
  User,
  MessageSquare,
  BarChart3,
  TrendingUp,
  DollarSign,
  MapPin,
  Building2,
  Phone,
  Calendar,
  Target,
  Award,
  AlertTriangle,
  Lightbulb,
  Users,
  TrendingDown,
  ShieldAlert,
  Star,
  CheckSquare,
  XCircle,
  Languages,
  ThumbsUp,
  MessageCircle,
  Zap,
  Activity
} from 'lucide-react';
import sampleReaData from '../data/sample_rea.json';
import transcriptData from '../data/transcript.json';

/**
 * RealEstateAnalysisPage Component
 * 
 * Features:
 * - MP3 file upload for sales call transcription
 * - Real-time audio playback
 * - Transcription via Soniox API
 * - Deep analysis of real estate conversations
 * - Modern, clean UI with professional design
 * - Test mode with query parameter ?test=true
 */
const RealEstateAnalysisPage = () => {
  // Test mode state
  const [isTestMode, setIsTestMode] = useState(false);

  // Configuration state
  const [config, setConfig] = useState({
    transcribeUrl: process.env.REACT_APP_TRANSCRIBE_URL || 'https://voicefuncappv26041.azurewebsites.net/api/soniox/transcribe-async',
    analyzeUrl: process.env.REACT_APP_ANALYZE_URL || 'http://localhost:8000/query/form',
    configPath: 'config\\real_estate_lead_analysis_fixed.yaml',
    enableSpeakerDiarization: true,
    languageHints: 'en',
    rawOutput: 'True'
  });

  // File and audio state
  const [selectedFile, setSelectedFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  // Transcription state
  const [transcription, setTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(null);

  // Analysis state
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // UI state
  const [showConfig, setShowConfig] = useState(false);

  // Check for test mode on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');
    if (testParam === 'true') {
      setIsTestMode(true);
      // In test mode, simulate a file upload
      setSelectedFile({ name: 'sample-real-estate-call.mp3', size: 1024000, type: 'audio/mpeg' });
      setAudioUrl('test-mode'); // Placeholder URL
    }
  }, []);

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'audio/mpeg') {
      setSelectedFile(file);
      // Create audio URL for playback
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
      // Reset states
      setTranscription(null);
      setAnalysis(null);
      setTranscriptionError(null);
      setAnalysisError(null);
      setIsPlaying(false);
      setCurrentTime(0);
    } else {
      alert('Please select a valid MP3 file');
    }
  };

  // Handle file removal
  const handleRemoveFile = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setSelectedFile(null);
    setAudioUrl(null);
    setTranscription(null);
    setAnalysis(null);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Audio control functions
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle transcription
  const handleTranscribe = async () => {
    if (!selectedFile) {
      setTranscriptionError('Please select an MP3 file first');
      return;
    }

    setIsTranscribing(true);
    setTranscriptionError(null);
    setTranscription(null);

    try {
      // Test mode: use sample data
      if (isTestMode) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setTranscription(transcriptData);
      } else {
        // Production mode: call actual API
        const formData = new FormData();
        formData.append('audio', selectedFile);

        const url = new URL(config.transcribeUrl);
        url.searchParams.append('enable_speaker_diarization', config.enableSpeakerDiarization);
        url.searchParams.append('language_hints', config.languageHints);

        const response = await fetch(url.toString(), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Transcription failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.status === 'success') {
          setTranscription(data);
        } else {
          throw new Error(data.message || 'Transcription failed');
        }
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setTranscriptionError(err.message || 'Failed to transcribe audio. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Handle analysis
  const handleAnalyze = async () => {
    // In test mode, allow analysis without transcription
    if (!isTestMode && !transcription) {
      setAnalysisError('Please transcribe the audio first');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysis(null);

    try {
      // Test mode: use sample data
      if (isTestMode) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        setAnalysis(sampleReaData);
      } else {
        // Production mode: call actual API
        const formData = new FormData();
        
        // Prepare the input object matching the transcription format
        const inputData = {
          status: 'success',
          transcription: transcription.transcription,
          metadata: transcription.metadata
        };
        
        // Generate a unique thread_id
        const threadId = `ANVITA_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        formData.append('input', JSON.stringify(inputData));
        formData.append('config_path', config.configPath);
        formData.append('raw_output', config.rawOutput);
        formData.append('thread_id', threadId);

        const response = await fetch(config.analyzeUrl, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Analysis failed: ${response.status} ${response.statusText}`);
        }

        // Get the response as text first to handle potential markdown code blocks
        let responseText = await response.text();
        
        // Strip markdown code blocks if present (```json ... ```)
        if (responseText.trim().startsWith('```')) {
          // Remove opening ```json or ``` and closing ```
          responseText = responseText
            .replace(/^```json?\s*/i, '')
            .replace(/\s*```\s*$/, '')
            .trim();
        }
        
        // Parse the cleaned JSON
        const data = JSON.parse(responseText);
        setAnalysis(data);
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setAnalysisError(err.message || 'Failed to analyze transcription. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Render file upload section
  const renderFileUpload = () => (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 mb-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
          <FileAudio className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-2xl font-semibold text-white">Upload Sales Call Recording</h2>
      </div>

      {!selectedFile ? (
        <div className="border-2 border-dashed border-white/30 rounded-xl p-12 text-center hover:border-blue-400 transition-colors duration-300">
          <Upload className="w-16 h-16 text-white/50 mx-auto mb-4" />
          <p className="text-white mb-4">Click to upload or drag and drop</p>
          <p className="text-sm text-white/60 mb-6">MP3 files only</p>
          <label className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors duration-200">
            Select MP3 File
            <input
              type="file"
              accept="audio/mpeg"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>
      ) : (
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3 flex-1">
              <FileAudio className="w-8 h-8 text-blue-400" />
              <div className="flex-1">
                <p className="text-white font-medium">{selectedFile.name}</p>
                <p className="text-sm text-white/60">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Audio player */}
          {audioUrl && (
            <div className="space-y-3">
              <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleAudioTimeUpdate}
                onLoadedMetadata={handleAudioLoadedMetadata}
                onEnded={handleAudioEnded}
              />
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={togglePlayPause}
                  className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-white" />
                  ) : (
                    <Play className="w-6 h-6 text-white ml-1" />
                  )}
                </button>
                
                <div className="flex-1">
                  <div className="flex justify-between text-sm text-white/60 mb-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-100"
                      style={{ width: `${(currentTime / audioDuration) * 100 || 0}%` }}
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleTranscribe}
                disabled={isTranscribing}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Transcribing...</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    <span>Transcribe Audio</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render transcription results
  const renderTranscription = () => {
    if (!transcription && !isTestMode) return null;

    // In test mode without transcription, show a simplified analyze button
    if (isTestMode && !transcription) {
      return (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 mb-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white">Ready for Analysis</h2>
              <p className="text-sm text-white/60">Click below to see sample analysis</p>
            </div>
          </div>

          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <BarChart3 className="w-5 h-5" />
                <span>Analyze Conversation</span>
              </>
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-white">Transcription Results</h2>
          </div>
          <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">
            Success
          </span>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-white/60">Duration</span>
            </div>
            <p className="text-white font-medium">
              {(transcription.metadata.audio_duration_ms / 1000).toFixed(1)}s
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <User className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-white/60">Model</span>
            </div>
            <p className="text-white font-medium text-sm">
              {transcription.metadata.model}
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <MessageSquare className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-white/60">Diarization</span>
            </div>
            <p className="text-white font-medium">
              {transcription.metadata.enable_speaker_diarization ? 'Enabled' : 'Disabled'}
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="w-4 h-4 text-green-400" />
              <span className="text-sm text-white/60">Created</span>
            </div>
            <p className="text-white font-medium text-sm">
              {new Date(transcription.metadata.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Transcription text */}
        <div className="bg-white/5 rounded-lg p-6 border border-white/10 mb-6">
          <h3 className="text-lg font-medium text-white mb-3 flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-blue-400" />
            <span>Transcription</span>
          </h3>
          <div className="text-white/90 leading-relaxed max-h-96 overflow-y-auto">
            {transcription.transcription}
          </div>
        </div>

        {/* Analyze button */}
        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <BarChart3 className="w-5 h-5" />
              <span>Analyze Conversation</span>
            </>
          )}
        </button>
      </div>
    );
  };

  // Render analysis results - Enhanced comprehensive version
  const renderAnalysis = () => {
    if (!analysis) return null;

    // Helper functions
    const getScoreColor = (score, maxScore = 10) => {
      const percentage = (score / maxScore) * 100;
      if (percentage >= 70) return 'text-green-400';
      if (percentage >= 40) return 'text-yellow-400';
      return 'text-red-400';
    };

    const getGradeColor = (grade) => {
      const gradeMap = {
        'A': 'text-green-400 bg-green-500/20 border-green-500',
        'B': 'text-blue-400 bg-blue-500/20 border-blue-500',
        'C': 'text-yellow-400 bg-yellow-500/20 border-yellow-500',
        'D': 'text-orange-400 bg-orange-500/20 border-orange-500',
        'F': 'text-red-400 bg-red-500/20 border-red-500'
      };
      return gradeMap[grade] || 'text-gray-400 bg-gray-500/20 border-gray-500';
    };

    const getPriorityColor = (priority) => {
      const priorityMap = {
        'High': 'bg-red-500/20 text-red-300 border-red-500/50',
        'Medium': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
        'Low': 'bg-blue-500/20 text-blue-300 border-blue-500/50'
      };
      return priorityMap[priority] || 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    };

    const getMatchScoreColor = (score) => {
      // Handle both 10-point (e.g., 8) and 100-point (e.g., 80) scales
      const normalizedScore = score > 10 ? score : score * 10;
      if (normalizedScore >= 80) return 'bg-green-500/20 text-green-300 border-green-500/50';
      if (normalizedScore >= 60) return 'bg-blue-500/20 text-blue-300 border-blue-500/50';
      if (normalizedScore >= 40) return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
      return 'bg-orange-500/20 text-orange-300 border-orange-500/50';
    };

    // Circular progress component
    const CircularProgress = ({ score, maxScore, label, icon: Icon }) => {
      const percentage = (score / maxScore) * 100;
      const circumference = 2 * Math.PI * 40;
      const strokeDashoffset = circumference - (percentage / 100) * circumference;

      return (
        <div className="flex flex-col items-center">
          <div className="relative w-28 h-28">
            <svg className="transform -rotate-90 w-28 h-28">
              <circle
                cx="56"
                cy="56"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-white/10"
              />
              <circle
                cx="56"
                cy="56"
                r="40"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className={`${getScoreColor(score, maxScore)} transition-all duration-1000 ease-out`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <Icon className={`w-6 h-6 mb-1 ${getScoreColor(score, maxScore)}`} />
              <span className={`text-2xl font-bold ${getScoreColor(score, maxScore)}`}>
                {score}
              </span>
              <span className="text-xs text-white/50">/ {maxScore}</span>
            </div>
          </div>
          <p className="text-sm text-white/70 mt-3 text-center">{label}</p>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {/* Key Metrics Dashboard - NEW */}
        <div className="bg-gradient-to-br from-gray-900/60 to-gray-800/60 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
            <Activity className="w-7 h-7 text-blue-400" />
            <span>Lead Dashboard</span>
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Lead Grade */}
            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 rounded-xl p-4 border-2 border-purple-500/50">
              <div className="text-xs text-purple-200 mb-1">Lead Grade</div>
              <div className={`text-4xl font-bold ${getGradeColor(analysis.lead_grade || analysis.lead_assessment?.lead_grade || analysis.executive_summary?.lead_grade).split(' ')[0]}`}>
                {analysis.lead_grade || analysis.lead_assessment?.lead_grade || analysis.executive_summary?.lead_grade}
              </div>
            </div>
            
            {/* Overall Score */}
            {(analysis.lead_assessment?.overall_score || analysis.executive_summary?.overall_score) && (
              <div className="bg-gradient-to-br from-blue-900/40 to-indigo-900/40 rounded-xl p-4 border-2 border-blue-500/50">
                <div className="text-xs text-blue-200 mb-1">Overall Score</div>
                <div className="text-4xl font-bold text-blue-300">
                  {analysis.lead_assessment?.overall_score || analysis.executive_summary?.overall_score}
                  <span className="text-lg text-blue-400">/10</span>
                </div>
              </div>
            )}
            
            {/* Conversion Probability */}
            {(analysis.conversion_probability || analysis.lead_assessment?.conversion_probability) && (
              <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-xl p-4 border-2 border-green-500/50">
                <div className="text-xs text-green-200 mb-1">Conversion</div>
                <div className="text-3xl font-bold text-green-300">
                  {(() => {
                    const prob = analysis.conversion_probability || analysis.lead_assessment?.conversion_probability;
                    if (typeof prob === 'number') return `${(prob * 100).toFixed(0)}%`;
                    return prob;
                  })()}
                </div>
              </div>
            )}
            
            {/* Urgency Score */}
            {(analysis.urgency_score || analysis.lead_assessment?.urgency_score || analysis.lead_assessment?.score_breakdown?.urgency_score) && (
              <div className="bg-gradient-to-br from-orange-900/40 to-red-900/40 rounded-xl p-4 border-2 border-orange-500/50">
                <div className="text-xs text-orange-200 mb-1">Urgency</div>
                <div className="text-4xl font-bold text-orange-300">
                  {analysis.urgency_score || analysis.lead_assessment?.urgency_score || analysis.lead_assessment?.score_breakdown?.urgency_score}
                  <span className="text-lg text-orange-400">/10</span>
                </div>
              </div>
            )}
            
            {/* Readiness to Buy */}
            {analysis.lead_assessment?.readiness_to_buy && (
              <div className="bg-gradient-to-br from-yellow-900/40 to-amber-900/40 rounded-xl p-4 border-2 border-yellow-500/50">
                <div className="text-xs text-yellow-200 mb-1">Readiness</div>
                <div className="text-xl font-bold text-yellow-300">
                  {analysis.lead_assessment.readiness_to_buy}
                </div>
              </div>
            )}
            
            {/* Follow-up Priority */}
            {analysis.lead_assessment?.follow_up_priority && (
              <div className="bg-gradient-to-br from-red-900/40 to-pink-900/40 rounded-xl p-4 border-2 border-red-500/50">
                <div className="text-xs text-red-200 mb-1">Priority</div>
                <div className="text-xl font-bold text-red-300">
                  {analysis.lead_assessment.follow_up_priority}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Executive Summary - Enhanced */}
        {(analysis.executive_summary || analysis.executive_summary?.summary) && (
          <div className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 backdrop-blur-md rounded-2xl shadow-lg border border-indigo-500/30 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white">Executive Summary</h2>
                <p className="text-sm text-indigo-200">AI-Generated Lead Intelligence</p>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 border border-indigo-400/30">
              <p className="text-white/90 leading-relaxed text-lg">
                {typeof analysis.executive_summary === 'string' 
                  ? analysis.executive_summary 
                  : analysis.executive_summary?.summary}
              </p>
            </div>
            
            {/* Additional executive summary details if available */}
            {analysis.executive_summary?.next_step && (
              <div className="mt-4 bg-indigo-500/20 rounded-xl p-4 border border-indigo-400/40">
                <div className="flex items-start space-x-3">
                  <Zap className="w-6 h-6 text-yellow-300 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-indigo-200 font-semibold mb-1">Immediate Next Step</h4>
                    <p className="text-white text-lg">{analysis.executive_summary.next_step}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conversation Analysis - NEW */}
        {analysis.conversation_analysis && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Conversation Analysis</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Sentiment */}
              {analysis.conversation_analysis.sentiment && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <ThumbsUp className="w-5 h-5 text-green-400" />
                      <span>Sentiment</span>
                    </h4>
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                      analysis.conversation_analysis.sentiment === 'Positive' ? 'bg-green-500/20 text-green-300' :
                      analysis.conversation_analysis.sentiment === 'Neutral' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-red-500/20 text-red-300'
                    }`}>
                      {analysis.conversation_analysis.sentiment}
                    </span>
                  </div>
                  <p className="text-white/70 text-sm">
                    {analysis.conversation_analysis.sentiment === 'Positive' && 'Customer shows positive attitude and engagement'}
                    {analysis.conversation_analysis.sentiment === 'Neutral' && 'Customer maintains neutral stance'}
                    {analysis.conversation_analysis.sentiment === 'Negative' && 'Customer shows concerns or negative sentiment'}
                  </p>
                </div>
              )}

              {/* Conversation Quality */}
              {analysis.conversation_analysis.conversation_quality && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <span>Call Quality</span>
                    </h4>
                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                      analysis.conversation_analysis.conversation_quality === 'Excellent' || analysis.conversation_analysis.conversation_quality === 'Good' 
                        ? 'bg-green-500/20 text-green-300' 
                        : analysis.conversation_analysis.conversation_quality === 'Average' 
                        ? 'bg-yellow-500/20 text-yellow-300' 
                        : 'bg-orange-500/20 text-orange-300'
                    }`}>
                      {analysis.conversation_analysis.conversation_quality}
                    </span>
                  </div>
                  <p className="text-white/70 text-sm">
                    {analysis.conversation_analysis.conversation_quality === 'Excellent' && 'Outstanding conversation with clear communication'}
                    {analysis.conversation_analysis.conversation_quality === 'Good' && 'Well-conducted conversation with good engagement'}
                    {analysis.conversation_analysis.conversation_quality === 'Average' && 'Adequate conversation, room for improvement'}
                    {analysis.conversation_analysis.conversation_quality === 'Poor' && 'Conversation needs significant improvement'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Header with Lead Grade */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white">Lead Assessment</h2>
                {analysis.lead_assessment && (
                  <p className="text-sm text-white/60">
                    {analysis.lead_assessment.qualification_status} • {analysis.lead_assessment.readiness_to_buy}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <div className={`px-6 py-3 rounded-xl border-2 ${getGradeColor(analysis.lead_grade || analysis.lead_assessment?.lead_grade || analysis.executive_summary?.lead_grade)}`}>
                <span className="text-sm font-medium">Lead Grade</span>
                <span className="text-3xl font-bold ml-3">{analysis.lead_grade || analysis.lead_assessment?.lead_grade || analysis.executive_summary?.lead_grade}</span>
              </div>
              {(analysis.lead_assessment?.overall_score || analysis.executive_summary?.overall_score) && (
                <div className="text-right">
                  <span className="text-white/60 text-sm">Overall Score: </span>
                  <span className="text-white font-semibold text-lg">{analysis.lead_assessment?.overall_score || analysis.executive_summary?.overall_score}/10</span>
                </div>
              )}
            </div>
          </div>

          {/* Detailed Score Breakdown */}
          {(analysis.lead_assessment || analysis.lead_assessment?.score_breakdown) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {(analysis.lead_assessment?.financial_score !== undefined || analysis.lead_assessment?.score_breakdown?.financial_score !== undefined) && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <span className={`text-2xl font-bold ${getScoreColor(analysis.lead_assessment?.financial_score || analysis.lead_assessment?.score_breakdown?.financial_score)}`}>
                      {analysis.lead_assessment?.financial_score || analysis.lead_assessment?.score_breakdown?.financial_score}
                    </span>
                  </div>
                  <p className="text-xs text-white/60">Financial Score</p>
                  <p className="text-xs text-white/40 mt-1">Budget clarity & affordability</p>
                </div>
              )}
              {(analysis.lead_assessment?.urgency_score !== undefined || analysis.lead_assessment?.score_breakdown?.urgency_score !== undefined) && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <span className={`text-2xl font-bold ${getScoreColor(analysis.lead_assessment?.urgency_score || analysis.lead_assessment?.score_breakdown?.urgency_score)}`}>
                      {analysis.lead_assessment?.urgency_score || analysis.lead_assessment?.score_breakdown?.urgency_score}
                    </span>
                  </div>
                  <p className="text-xs text-white/60">Urgency Score</p>
                  <p className="text-xs text-white/40 mt-1">Timeline & decision speed</p>
                </div>
              )}
              {(analysis.lead_assessment?.engagement_score !== undefined || analysis.lead_assessment?.score_breakdown?.engagement_score !== undefined) && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    <span className={`text-2xl font-bold ${getScoreColor(analysis.lead_assessment?.engagement_score || analysis.lead_assessment?.score_breakdown?.engagement_score)}`}>
                      {analysis.lead_assessment?.engagement_score || analysis.lead_assessment?.score_breakdown?.engagement_score}
                    </span>
                  </div>
                  <p className="text-xs text-white/60">Engagement Score</p>
                  <p className="text-xs text-white/40 mt-1">Interaction quality</p>
                </div>
              )}
              {(analysis.lead_assessment?.fit_score !== undefined || analysis.lead_assessment?.score_breakdown?.fit_score !== undefined) && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <Target className="w-5 h-5 text-purple-400" />
                    <span className={`text-2xl font-bold ${getScoreColor(analysis.lead_assessment?.fit_score || analysis.lead_assessment?.score_breakdown?.fit_score)}`}>
                      {analysis.lead_assessment?.fit_score || analysis.lead_assessment?.score_breakdown?.fit_score}
                    </span>
                  </div>
                  <p className="text-xs text-white/60">Fit Score</p>
                  <p className="text-xs text-white/40 mt-1">Requirements match</p>
                </div>
              )}
            </div>
          )}

          {/* Conversion Probability */}
          <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 rounded-xl p-6 border border-purple-500/30 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-8 h-8 text-purple-400" />
                <div>
                  <h3 className="text-xl font-semibold text-white">Conversion Probability</h3>
                  <p className="text-sm text-white/60">Likelihood of closing this deal</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-purple-300">
                  {(() => {
                    const prob = analysis.conversion_probability || analysis.lead_assessment?.conversion_probability;
                    if (typeof prob === 'number') {
                      return `${(prob * 100).toFixed(0)}%`;
                    } else if (typeof prob === 'string') {
                      return prob;
                    }
                    return 'N/A';
                  })()}
                </div>
                <div className="text-sm text-white/60 mt-1">
                  {(() => {
                    const prob = analysis.conversion_probability || analysis.lead_assessment?.conversion_probability;
                    if (typeof prob === 'string') return '';
                    if (typeof prob === 'number') {
                      if (prob >= 0.7) return 'High';
                      if (prob >= 0.4) return 'Medium';
                      return 'Low';
                    }
                    return '';
                  })()}
                </div>
              </div>
            </div>
            <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden">
              <div
                className="h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000 ease-out"
                style={{ 
                  width: `${(() => {
                    const prob = analysis.conversion_probability || analysis.lead_assessment?.conversion_probability;
                    if (typeof prob === 'number') return prob * 100;
                    if (prob === 'High') return 80;
                    if (prob === 'Medium') return 50;
                    if (prob === 'Low') return 20;
                    return 0;
                  })()}%` 
                }}
              />
            </div>
          </div>

          {/* Analysis Summary */}
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-start space-x-3">
              <MessageSquare className="w-5 h-5 text-blue-400 mt-1" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/70 mb-1">Analysis Summary</h4>
                <p className="text-white leading-relaxed">
                  {analysis.reason || analysis.lead_assessment?.strengths?.[0] || 'Analysis complete'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Profile - NEW */}
        {analysis.customer_profile && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Customer Profile</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Property Requirements */}
              {analysis.customer_profile.property_requirements && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                    <Building2 className="w-5 h-5 text-orange-400" />
                    <span>Property Requirements</span>
                  </h4>
                  <div className="space-y-3">
                    {analysis.customer_profile.property_requirements.property_type && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Property Type</span>
                        <span className="text-white font-medium">{analysis.customer_profile.property_requirements.property_type}</span>
                      </div>
                    )}
                    {analysis.customer_profile.property_requirements.location_preference && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Location</span>
                        <span className="text-white font-medium">{analysis.customer_profile.property_requirements.location_preference}</span>
                      </div>
                    )}
                    {analysis.customer_profile.property_requirements.size_preference && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Size</span>
                        <span className="text-white font-medium">{analysis.customer_profile.property_requirements.size_preference}</span>
                      </div>
                    )}
                    {analysis.customer_profile.property_requirements.under_construction !== undefined && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Under Construction</span>
                        <span className={`font-medium ${analysis.customer_profile.property_requirements.under_construction === 'Yes' ? 'text-green-300' : 'text-red-300'}`}>
                          {analysis.customer_profile.property_requirements.under_construction}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Financial Details */}
              {analysis.customer_profile.financial_details && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <span>Financial Details</span>
                  </h4>
                  <div className="space-y-3">
                    {analysis.customer_profile.financial_details.budget && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Budget</span>
                        <span className="text-white font-medium">{analysis.customer_profile.financial_details.budget}</span>
                      </div>
                    )}
                    {analysis.customer_profile.financial_details.loan_requirement && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Loan Requirement</span>
                        <span className="text-white font-medium">{analysis.customer_profile.financial_details.loan_requirement}</span>
                      </div>
                    )}
                    {analysis.customer_profile.financial_details.payment_plan && (
                      <div className="col-span-2">
                        <span className="text-white/60 text-sm block mb-1">Payment Plan</span>
                        <span className="text-white font-medium text-sm">{analysis.customer_profile.financial_details.payment_plan}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {analysis.customer_profile.timeline && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-purple-400" />
                    <span>Timeline</span>
                  </h4>
                  <div className="space-y-3">
                    {analysis.customer_profile.timeline.purchase_timeline && (
                      <div>
                        <span className="text-white/60 text-sm block mb-1">Purchase Timeline</span>
                        <span className="text-white font-medium">{analysis.customer_profile.timeline.purchase_timeline}</span>
                      </div>
                    )}
                    {analysis.customer_profile.timeline.site_visit_timeline && (
                      <div>
                        <span className="text-white/60 text-sm block mb-1">Site Visit</span>
                        <span className="text-white font-medium">{analysis.customer_profile.timeline.site_visit_timeline}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Engagement Signals */}
              {analysis.customer_profile.engagement_signals && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-pink-400" />
                    <span>Engagement Signals</span>
                  </h4>
                  <div className="space-y-3">
                    {analysis.customer_profile.engagement_signals.expressed_interest && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Interest Level</span>
                        <span className="text-green-300 font-medium">{analysis.customer_profile.engagement_signals.expressed_interest}</span>
                      </div>
                    )}
                    {analysis.customer_profile.engagement_signals.sentiment && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/60 text-sm">Sentiment</span>
                        <span className="text-white font-medium">{analysis.customer_profile.engagement_signals.sentiment}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Questions Asked */}
            {(() => {
              const questions = analysis.customer_profile.questions_asked || analysis.customer_profile.engagement_signals?.questions || analysis.customer_profile.questions;
              return questions && questions.length > 0 && (
                <div className="mt-6 bg-blue-900/20 rounded-xl p-5 border border-blue-500/30">
                  <h4 className="text-lg font-semibold text-blue-300 mb-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="w-5 h-5 text-blue-400" />
                      <span>Questions Asked by Customer</span>
                    </div>
                    <span className="px-3 py-1 bg-blue-500/30 text-blue-200 rounded-full text-sm font-bold">
                      {questions.length}
                    </span>
                  </h4>
                  <div className="space-y-3">
                    {questions.map((question, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-3 border border-blue-500/20">
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-blue-300 font-bold text-xs">{idx + 1}</span>
                          </div>
                          <p className="text-white/90 text-sm leading-relaxed">{question}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <p className="text-blue-200 text-xs">
                      💡 These questions indicate the customer's level of interest and decision-making criteria
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Concerns & Objections - Enhanced */}
            {(() => {
              const concerns = analysis.customer_profile.concerns_and_objections?.primary_concern || 
                             analysis.customer_profile.engagement_signals?.concerns || 
                             analysis.customer_profile.concerns;
              const objections = analysis.customer_profile.concerns_and_objections?.objections ||
                               analysis.customer_profile.engagement_signals?.objections || 
                               analysis.customer_profile.objections;
              
              // Handle single string concern
              const concernsArray = typeof concerns === 'string' ? [concerns] : concerns;
              const hasConcerns = concernsArray && (Array.isArray(concernsArray) ? concernsArray.length > 0 : true);
              const hasObjections = objections && objections.length > 0;
              
              return (hasConcerns || hasObjections) && (
                <div className="mt-6">
                  <h4 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                    <AlertTriangle className="w-6 h-6 text-orange-400" />
                    <span>Concerns & Objections to Address</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {hasConcerns && (
                      <div className="bg-yellow-900/30 rounded-xl p-5 border-2 border-yellow-500/50">
                        <h5 className="text-lg font-semibold text-yellow-300 mb-4 flex items-center space-x-2">
                          <AlertCircle className="w-5 h-5" />
                          <span>Customer Concerns</span>
                        </h5>
                        <ul className="space-y-3">
                          {(Array.isArray(concernsArray) ? concernsArray : [concernsArray]).map((concern, idx) => (
                            <li key={idx} className="flex items-start space-x-3 bg-yellow-900/20 rounded-lg p-3">
                              <AlertTriangle className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                              <span className="text-white/90 text-sm leading-relaxed">{concern}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {hasObjections && (
                      <div className="bg-red-900/30 rounded-xl p-5 border-2 border-red-500/50">
                        <h5 className="text-lg font-semibold text-red-300 mb-4 flex items-center space-x-2">
                          <XCircle className="w-5 h-5" />
                          <span>Objections</span>
                        </h5>
                        <ul className="space-y-3">
                          {objections.map((objection, idx) => (
                            <li key={idx} className="flex items-start space-x-3 bg-red-900/20 rounded-lg p-3">
                              <XCircle className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                              <span className="text-white/90 text-sm leading-relaxed">{objection}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-4 bg-orange-900/20 rounded-xl border border-orange-500/30">
                    <div className="flex items-start space-x-3">
                      <Lightbulb className="w-5 h-5 text-orange-300 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-orange-200 font-semibold text-sm mb-1">Action Required</p>
                        <p className="text-orange-100 text-sm">
                          Address these concerns proactively in your next interaction. Prepare detailed answers and supporting documents.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Lead Assessment Details - Strengths, Weaknesses, Risks, Opportunities */}
        {analysis.lead_assessment && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-white">SWOR Analysis</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Strengths */}
              {analysis.lead_assessment.strengths && analysis.lead_assessment.strengths.length > 0 && (
                <div className="bg-green-900/20 rounded-xl p-5 border border-green-500/30">
                  <h4 className="text-lg font-semibold text-green-300 mb-4 flex items-center space-x-2">
                    <ThumbsUp className="w-5 h-5" />
                    <span>Strengths</span>
                  </h4>
                  <ul className="space-y-2">
                    {analysis.lead_assessment.strengths.map((strength, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/90">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {analysis.lead_assessment.weaknesses && analysis.lead_assessment.weaknesses.length > 0 && (
                <div className="bg-orange-900/20 rounded-xl p-5 border border-orange-500/30">
                  <h4 className="text-lg font-semibold text-orange-300 mb-4 flex items-center space-x-2">
                    <TrendingDown className="w-5 h-5" />
                    <span>Weaknesses</span>
                  </h4>
                  <ul className="space-y-2">
                    {analysis.lead_assessment.weaknesses.map((weakness, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/90">{weakness}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Opportunities */}
              {analysis.lead_assessment.opportunities && analysis.lead_assessment.opportunities.length > 0 && (
                <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-500/30">
                  <h4 className="text-lg font-semibold text-blue-300 mb-4 flex items-center space-x-2">
                    <Lightbulb className="w-5 h-5" />
                    <span>Opportunities</span>
                  </h4>
                  <ul className="space-y-2">
                    {analysis.lead_assessment.opportunities.map((opportunity, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <Zap className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/90">{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risks */}
              {analysis.lead_assessment.risks && analysis.lead_assessment.risks.length > 0 && (
                <div className="bg-red-900/20 rounded-xl p-5 border border-red-500/30">
                  <h4 className="text-lg font-semibold text-red-300 mb-4 flex items-center space-x-2">
                    <ShieldAlert className="w-5 h-5" />
                    <span>Risks</span>
                  </h4>
                  <ul className="space-y-2">
                    {analysis.lead_assessment.risks.map((risk, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/90">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detailed Scores - Keep existing */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
            <BarChart3 className="w-6 h-6 text-blue-400" />
            <span>Lead Quality Scores</span>
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <CircularProgress 
              score={analysis.urgency_score} 
              maxScore={10} 
              label="Urgency Score" 
              icon={Clock}
            />
            <CircularProgress 
              score={analysis.budget_score} 
              maxScore={10} 
              label="Budget Score" 
              icon={DollarSign}
            />
            <CircularProgress 
              score={analysis.location_score} 
              maxScore={10} 
              label="Location Score" 
              icon={MapPin}
            />
            <CircularProgress 
              score={analysis.project_match_score} 
              maxScore={10} 
              label="Project Match" 
              icon={Building2}
            />
          </div>

          {/* Score Interpretation */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white/70 mb-2">Overall Assessment</h4>
              <p className="text-white text-sm">
                {analysis.lead_grade === 'A' && 'Excellent lead with high conversion potential. Priority follow-up recommended.'}
                {analysis.lead_grade === 'B' && 'Strong lead with good conversion potential. Schedule follow-up within 24 hours.'}
                {analysis.lead_grade === 'C' && 'Average lead with moderate potential. Continue nurturing with regular follow-ups.'}
                {analysis.lead_grade === 'D' && 'Below average lead. Requires additional qualification before investment.'}
                {analysis.lead_grade === 'F' && 'Low quality lead. Minimal follow-up resources recommended.'}
              </p>
            </div>
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white/70 mb-2">Key Strengths</h4>
              <div className="space-y-1">
                {analysis.urgency_score >= 7 && (
                  <div className="flex items-center space-x-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>High urgency to purchase</span>
                  </div>
                )}
                {analysis.budget_score >= 7 && (
                  <div className="flex items-center space-x-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Budget well aligned</span>
                  </div>
                )}
                {analysis.location_score >= 7 && (
                  <div className="flex items-center space-x-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Location preferences match</span>
                  </div>
                )}
                {analysis.project_match_score >= 7 && (
                  <div className="flex items-center space-x-2 text-green-300 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Excellent project fit</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Project Matching Section - Enhanced */}
        {(analysis.project_match || analysis.project_matching) && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
              <Building2 className="w-6 h-6 text-orange-400" />
              <span>Project Matching & Rankings</span>
            </h3>
            
            {/* Project Rankings from new format */}
            {(() => {
              const projects = analysis.project_matching?.ranked_projects || analysis.project_matching?.project_rankings;
              return projects && projects.length > 0 ? (
                <div className="space-y-4">
                  {projects.map((project, index) => (
                  <div 
                    key={index}
                    className={`rounded-xl p-6 border ${
                      index === 0 
                        ? 'bg-gradient-to-br from-orange-900/30 to-yellow-900/30 border-orange-500/50' 
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-xl font-semibold text-white">
                            {project.project_name}
                          </h4>
                          {index === 0 && (
                            <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium flex items-center space-x-1">
                              <Star className="w-3 h-3" />
                              <span>Best Match</span>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className={`px-4 py-2 rounded-lg border ${getMatchScoreColor(project.match_score)}`}>
                            <span className="text-sm">Match Score: </span>
                            <span className="text-xl font-bold">{project.match_score}</span>
                            <span className="text-sm">/{project.match_score > 10 ? '100' : '10'}</span>
                          </div>
                          <span className="text-white/40 text-sm">Rank #{index + 1}</span>
                        </div>
                      </div>
                      <Building2 className={`w-12 h-12 ${index === 0 ? 'text-orange-400/70' : 'text-white/20'}`} />
                    </div>

                    {/* Fit Analysis */}
                    {project.fit_analysis && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        {project.fit_analysis.price && (
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="flex items-center space-x-2 mb-2">
                              <DollarSign className="w-4 h-4 text-green-400" />
                              <span className="text-xs text-white/60 font-medium">Price Fit</span>
                            </div>
                            <p className="text-xs text-white/80">{project.fit_analysis.price}</p>
                          </div>
                        )}
                        {project.fit_analysis.location && (
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="flex items-center space-x-2 mb-2">
                              <MapPin className="w-4 h-4 text-blue-400" />
                              <span className="text-xs text-white/60 font-medium">Location Fit</span>
                            </div>
                            <p className="text-xs text-white/80">{project.fit_analysis.location}</p>
                          </div>
                        )}
                        {project.fit_analysis.timeline && (
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="flex items-center space-x-2 mb-2">
                              <Clock className="w-4 h-4 text-purple-400" />
                              <span className="text-xs text-white/60 font-medium">Timeline Fit</span>
                            </div>
                            <p className="text-xs text-white/80">{project.fit_analysis.timeline}</p>
                          </div>
                        )}
                        {project.fit_analysis.type && (
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="flex items-center space-x-2 mb-2">
                              <Building2 className="w-4 h-4 text-orange-400" />
                              <span className="text-xs text-white/60 font-medium">Type Fit</span>
                            </div>
                            <p className="text-xs text-white/80">{project.fit_analysis.type}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Alternative Matches */}
                {analysis.project_matching.alternative_matches && analysis.project_matching.alternative_matches.length > 0 && (
                  <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-500/30 mt-4">
                    <h4 className="text-sm font-semibold text-blue-300 mb-3 flex items-center space-x-2">
                      <Lightbulb className="w-4 h-4" />
                      <span>Alternative Options</span>
                    </h4>
                    <ul className="space-y-2">
                      {analysis.project_matching.alternative_matches.map((alt, idx) => (
                        <li key={idx} className="text-white/80 text-sm flex items-start space-x-2">
                          <CheckSquare className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span>{alt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              // Fallback to old format
              analysis.project_match && analysis.project_match.length > 0 && (
                <div className="space-y-4">
                  {analysis.project_match.map((project, index) => (
                    <div 
                      key={index}
                      className="bg-gradient-to-br from-orange-900/20 to-yellow-900/20 rounded-xl p-6 border border-orange-500/30"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-xl font-semibold text-orange-300 mb-1">
                            {project.project_name}
                          </h4>
                          <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium">
                            Best Match
                          </span>
                        </div>
                        <Building2 className="w-12 h-12 text-orange-400/50" />
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                        <p className="text-white/90 leading-relaxed">{project.match_reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            );
            })()}
          </div>
        )}

        {/* Action Plan Section - Enhanced with Timeline Categories */}
        {(analysis.action_plan?.immediate_actions || analysis.action_plan?.short_term_actions || 
          analysis.action_plan?.medium_term_actions || (Array.isArray(analysis.action_plan) && analysis.action_plan.length > 0)) && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
              <CheckSquare className="w-6 h-6 text-green-400" />
              <span>Action Plan & Next Steps</span>
            </h3>
            
            {/* New structured format */}
            {analysis.action_plan && !Array.isArray(analysis.action_plan) ? (
              <div className="space-y-6">
                {/* Immediate Actions */}
                {analysis.action_plan.immediate_actions && analysis.action_plan.immediate_actions.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Zap className="w-5 h-5 text-red-400" />
                      <h4 className="text-lg font-semibold text-red-300">Immediate Actions (Urgent)</h4>
                    </div>
                    <div className="space-y-3">
                      {analysis.action_plan.immediate_actions.map((action, idx) => (
                        <div key={idx} className="bg-red-900/20 rounded-xl p-4 border border-red-500/30">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-red-500/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                              <span className="text-red-300 font-bold text-sm">{idx + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium mb-2">{action.action}</p>
                              {action.details && (
                                <p className="text-white/70 text-sm mb-2">{action.details}</p>
                              )}
                              <div className="flex flex-wrap gap-2">
                                <span className="px-2 py-1 bg-red-500/20 text-red-300 border border-red-500/50 rounded text-xs flex items-center space-x-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{new Date(action.due_date).toLocaleDateString()}</span>
                                </span>
                                {action.responsible && (
                                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded text-xs flex items-center space-x-1">
                                    <Users className="w-3 h-3" />
                                    <span>{action.responsible}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Short Term Actions */}
                {analysis.action_plan.short_term_actions && analysis.action_plan.short_term_actions.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Clock className="w-5 h-5 text-yellow-400" />
                      <h4 className="text-lg font-semibold text-yellow-300">Short-Term Actions (This Week)</h4>
                    </div>
                    <div className="space-y-3">
                      {analysis.action_plan.short_term_actions.map((action, idx) => (
                        <div key={idx} className="bg-yellow-900/20 rounded-xl p-4 border border-yellow-500/30">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-yellow-500/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                              <span className="text-yellow-300 font-bold text-sm">{idx + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium mb-2">{action.action}</p>
                              {action.details && (
                                <p className="text-white/70 text-sm mb-2">{action.details}</p>
                              )}
                              <div className="flex flex-wrap gap-2">
                                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 border border-yellow-500/50 rounded text-xs flex items-center space-x-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{new Date(action.due_date).toLocaleDateString()}</span>
                                </span>
                                {action.responsible && (
                                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded text-xs flex items-center space-x-1">
                                    <Users className="w-3 h-3" />
                                    <span>{action.responsible}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Medium Term Actions */}
                {analysis.action_plan.medium_term_actions && analysis.action_plan.medium_term_actions.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-blue-400" />
                      <h4 className="text-lg font-semibold text-blue-300">Medium-Term Actions (This Month)</h4>
                    </div>
                    <div className="space-y-3">
                      {analysis.action_plan.medium_term_actions.map((action, idx) => (
                        <div key={idx} className="bg-blue-900/20 rounded-xl p-4 border border-blue-500/30">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-blue-500/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                              <span className="text-blue-300 font-bold text-sm">{idx + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium mb-2">{action.action}</p>
                              {action.details && (
                                <p className="text-white/70 text-sm mb-2">{action.details}</p>
                              )}
                              <div className="flex flex-wrap gap-2">
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded text-xs flex items-center space-x-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{new Date(action.due_date).toLocaleDateString()}</span>
                                </span>
                                {action.responsible && (
                                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 border border-purple-500/50 rounded text-xs flex items-center space-x-1">
                                    <Users className="w-3 h-3" />
                                    <span>{action.responsible}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Fallback to old format
              Array.isArray(analysis.action_plan) && (
                <div className="space-y-4">
                  {analysis.action_plan.map((action, index) => (
                    <div 
                      key={index}
                      className="bg-white/5 rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-white mb-2">{action.action}</h4>
                          <div className="flex flex-wrap gap-3 mb-3">
                            {action.priority && (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(action.priority)}`}>
                                {action.priority} Priority
                              </span>
                            )}
                            {action.due_date && (
                              <span className="px-3 py-1 bg-purple-500/20 text-purple-300 border border-purple-500/50 rounded-full text-xs font-medium flex items-center space-x-1">
                                <Calendar className="w-3 h-3" />
                                <span>Due: {new Date(action.due_date).toLocaleDateString()}</span>
                              </span>
                            )}
                            {action.assigned_to && (
                              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded-full text-xs font-medium flex items-center space-x-1">
                                <User className="w-3 h-3" />
                                <span>{action.assigned_to}</span>
                              </span>
                            )}
                          </div>
                          {action.notes && (
                            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                              <p className="text-sm text-white/70 leading-relaxed">
                                <span className="font-medium text-white">Notes: </span>
                                {action.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/* Strategic Guidance Section - Enhanced */}
        {(analysis.strategies || analysis.strategic_guidance) && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
              <Lightbulb className="w-6 h-6 text-yellow-400" />
              <span>Strategic Recommendations for Sales Team</span>
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Communication Strategy */}
              {(analysis.strategies?.communication_strategy || analysis.strategic_guidance?.communication_strategy) && (
                <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 rounded-xl p-6 border-2 border-blue-500/40">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-blue-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-blue-300 mb-1">Communication Strategy</h4>
                      <p className="text-blue-200 text-xs">How to engage with this lead</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">
                    {analysis.strategies?.communication_strategy || analysis.strategic_guidance?.communication_strategy}
                  </p>
                </div>
              )}
              
              {/* Objection Handling */}
              {(analysis.strategies?.objection_handling || analysis.strategic_guidance?.objection_handling) && (
                <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 rounded-xl p-6 border-2 border-orange-500/40">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ShieldAlert className="w-6 h-6 text-orange-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-orange-300 mb-1">Objection Handling</h4>
                      <p className="text-orange-200 text-xs">How to address concerns</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">
                    {analysis.strategies?.objection_handling || analysis.strategic_guidance?.objection_handling}
                  </p>
                </div>
              )}
              
              {/* Competitive Strategy */}
              {(analysis.strategies?.competitive_strategy || analysis.strategic_guidance?.competitive_strategy) && (
                <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl p-6 border-2 border-purple-500/40">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-purple-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-purple-300 mb-1">Competitive Strategy</h4>
                      <p className="text-purple-200 text-xs">Stand out from competition</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">
                    {analysis.strategies?.competitive_strategy || analysis.strategic_guidance?.competitive_strategy}
                  </p>
                </div>
              )}
              
              {/* Negotiation Strategy */}
              {(analysis.strategies?.negotiation_strategy || analysis.strategic_guidance?.negotiation_strategy) && (
                <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl p-6 border-2 border-green-500/40">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-6 h-6 text-green-300" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-300 mb-1">Negotiation Strategy</h4>
                      <p className="text-green-200 text-xs">Pricing and deal structure</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">
                    {analysis.strategies?.negotiation_strategy || analysis.strategic_guidance?.negotiation_strategy}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Site Visit Plan - Enhanced */}
        {analysis.site_visit_plan && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
              <MapPin className="w-6 h-6 text-green-400" />
              <span>Site Visit Execution Plan</span>
            </h3>
            
            {/* Objective & Pre-visit Briefing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {analysis.site_visit_plan.objective && (
                <div className="bg-green-900/20 rounded-xl p-5 border border-green-500/30">
                  <div className="flex items-center space-x-2 mb-3">
                    <Target className="w-5 h-5 text-green-400" />
                    <h4 className="text-lg font-semibold text-green-300">Visit Objective</h4>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">{analysis.site_visit_plan.objective}</p>
                </div>
              )}
              
              {analysis.site_visit_plan.pre_visit_briefing && (
                <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-500/30">
                  <div className="flex items-center space-x-2 mb-3">
                    <FileAudio className="w-5 h-5 text-blue-400" />
                    <h4 className="text-lg font-semibold text-blue-300">Team Briefing</h4>
                  </div>
                  <p className="text-white/90 text-sm leading-relaxed">{analysis.site_visit_plan.pre_visit_briefing}</p>
                </div>
              )}
            </div>

            {/* Visit Flow */}
            {analysis.site_visit_plan.visit_flow && Array.isArray(analysis.site_visit_plan.visit_flow) && (
              <div className="bg-purple-900/20 rounded-xl p-5 border border-purple-500/30 mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Activity className="w-5 h-5 text-purple-400" />
                  <h4 className="text-lg font-semibold text-purple-300">Visit Flow & Steps</h4>
                </div>
                <div className="space-y-3">
                  {analysis.site_visit_plan.visit_flow.map((step, idx) => (
                    <div key={idx} className="flex items-start space-x-3 bg-white/5 rounded-lg p-3">
                      <div className="w-8 h-8 bg-purple-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-purple-300 font-bold text-sm">{idx + 1}</span>
                      </div>
                      <p className="text-white/90 text-sm leading-relaxed flex-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Three Column Layout for Preparation, Execution, Post-Visit */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {analysis.site_visit_plan.preparation && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center space-x-2 mb-3">
                    <CheckSquare className="w-5 h-5 text-blue-400" />
                    <h4 className="text-md font-semibold text-blue-300">Preparation</h4>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">{analysis.site_visit_plan.preparation}</p>
                </div>
              )}
              
              {analysis.site_visit_plan.execution && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center space-x-2 mb-3">
                    <Play className="w-5 h-5 text-purple-400" />
                    <h4 className="text-md font-semibold text-purple-300">Execution</h4>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">{analysis.site_visit_plan.execution}</p>
                </div>
              )}
              
              {(analysis.site_visit_plan.post_visit || analysis.site_visit_plan.post_visit_debrief) && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center space-x-2 mb-3">
                    <MessageCircle className="w-5 h-5 text-orange-400" />
                    <h4 className="text-md font-semibold text-orange-300">Post-Visit</h4>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {analysis.site_visit_plan.post_visit || analysis.site_visit_plan.post_visit_debrief}
                  </p>
                </div>
              )}
            </div>

            {/* Location highlight */}
            {analysis.site_visit_plan.location && (
              <div className="p-4 bg-gradient-to-r from-green-900/30 to-teal-900/30 rounded-xl border-2 border-green-500/40">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-green-200 text-xs font-semibold mb-1">Visit Location</p>
                    <p className="text-white font-medium text-lg">{analysis.site_visit_plan.location}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Next Contact Schedule - Enhanced */}
        {analysis.next_contact_schedule && (
          <div className="bg-gradient-to-br from-pink-900/40 to-red-900/40 backdrop-blur-md rounded-2xl shadow-lg border-2 border-pink-500/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-white flex items-center space-x-3">
                <div className="w-10 h-10 bg-pink-500/30 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-pink-300" />
                </div>
                <span>Next Contact Scheduled</span>
              </h3>
              <span className="px-4 py-2 bg-red-500/30 text-red-200 rounded-full text-sm font-bold animate-pulse">
                URGENT
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {/* Next Action */}
              {analysis.next_contact_schedule.next_action && (
                <div className="bg-white/10 rounded-xl p-4 border border-pink-400/30 md:col-span-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <Zap className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-pink-200 font-medium">Next Action</span>
                  </div>
                  <p className="text-white font-semibold text-lg">
                    {analysis.next_contact_schedule.next_action}
                  </p>
                </div>
              )}

              {/* Channel */}
              {analysis.next_contact_schedule.channel && (
                <div className="bg-white/10 rounded-xl p-4 border border-pink-400/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <MessageSquare className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-pink-200 font-medium">Channel</span>
                  </div>
                  <p className="text-white font-semibold text-lg">
                    {analysis.next_contact_schedule.channel}
                  </p>
                </div>
              )}

              {/* Timing */}
              {analysis.next_contact_schedule.timing && (
                <div className="bg-white/10 rounded-xl p-4 border border-pink-400/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <span className="text-sm text-pink-200 font-medium">Timing</span>
                  </div>
                  <p className="text-white font-semibold text-lg">
                    {analysis.next_contact_schedule.timing}
                  </p>
                </div>
              )}

              {/* Date (if available) */}
              {analysis.next_contact_schedule.date && (
                <div className="bg-white/10 rounded-xl p-4 border border-pink-400/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-pink-200 font-medium">Date</span>
                  </div>
                  <p className="text-white font-semibold">
                    {new Date(analysis.next_contact_schedule.date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              )}
              
              {/* Time (if available) */}
              {analysis.next_contact_schedule.time && (
                <div className="bg-white/10 rounded-xl p-4 border border-pink-400/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <span className="text-sm text-pink-200 font-medium">Time</span>
                  </div>
                  <p className="text-white font-semibold text-lg">{analysis.next_contact_schedule.time}</p>
                </div>
              )}
              
              {/* Purpose/Objective */}
              {(analysis.next_contact_schedule.purpose || analysis.next_contact_schedule.objective) && (
                <div className="bg-white/10 rounded-xl p-4 border border-pink-400/30 md:col-span-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <Target className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-pink-200 font-medium">Objective</span>
                  </div>
                  <p className="text-white font-semibold">
                    {analysis.next_contact_schedule.purpose || analysis.next_contact_schedule.objective}
                  </p>
                </div>
              )}
            </div>

            {/* Action Alert */}
            <div className="p-4 bg-yellow-900/30 border-2 border-yellow-500/50 rounded-xl">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-yellow-200 font-semibold mb-1">⚡ Action Required</p>
                  <p className="text-yellow-100 text-sm">
                    Set a reminder and prepare all necessary materials before this contact. Review customer profile and questions above.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Translation Section */}
        {(analysis.translation || analysis.english_transcription || analysis.conversation_analysis?.translation) && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
              <Languages className="w-6 h-6 text-blue-400" />
              <span>Conversation Translation</span>
            </h3>
            <div className="bg-white/5 rounded-lg p-6 border border-white/10 max-h-96 overflow-y-auto">
              {/* Handle array format (new structure) */}
              {Array.isArray(analysis.conversation_analysis?.translation) ? (
                <div className="space-y-4">
                  {analysis.conversation_analysis.translation.map((entry, idx) => (
                    <div key={idx} className="border-l-4 border-blue-500/30 pl-4">
                      <p className="text-blue-300 text-sm font-semibold mb-1">{entry.speaker}</p>
                      <p className="text-white/90 leading-relaxed">{entry.dialogue}</p>
                    </div>
                  ))}
                </div>
              ) : (
                /* Handle string format (old structure) */
                <p className="text-white/90 leading-relaxed whitespace-pre-line">
                  {analysis.translation || analysis.english_transcription}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Metadata Section - NEW */}
        {analysis.metadata && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
              <FileAudio className="w-6 h-6 text-gray-400" />
              <span>Recording Metadata</span>
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Transcription ID */}
              {analysis.metadata.transcription_id && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10 md:col-span-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <Target className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-white/60">Transcription ID</span>
                  </div>
                  <p className="text-white font-mono text-xs break-all">
                    {analysis.metadata.transcription_id}
                  </p>
                </div>
              )}

              {/* File ID */}
              {analysis.metadata.file_id && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10 md:col-span-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <FileAudio className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-white/60">File ID</span>
                  </div>
                  <p className="text-white font-mono text-xs break-all">
                    {analysis.metadata.file_id}
                  </p>
                </div>
              )}

              {/* Filename */}
              {analysis.metadata.filename && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <FileAudio className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white/60">Filename</span>
                  </div>
                  <p className="text-white font-medium">
                    {analysis.metadata.filename}
                  </p>
                </div>
              )}

              {/* Audio Duration */}
              {analysis.metadata.audio_duration_ms && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-white/60">Duration</span>
                  </div>
                  <p className="text-white font-medium">
                    {(analysis.metadata.audio_duration_ms / 1000).toFixed(1)}s
                  </p>
                </div>
              )}

              {/* Model */}
              {analysis.metadata.model && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Activity className="w-4 h-4 text-pink-400" />
                    <span className="text-sm text-white/60">AI Model</span>
                  </div>
                  <p className="text-white font-medium text-sm">
                    {analysis.metadata.model}
                  </p>
                </div>
              )}

              {/* Language Hints */}
              {analysis.metadata.language_hints && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Languages className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-white/60">Languages</span>
                  </div>
                  <p className="text-white font-medium">
                    {Array.isArray(analysis.metadata.language_hints) 
                      ? analysis.metadata.language_hints.join(', ').toUpperCase()
                      : analysis.metadata.language_hints}
                  </p>
                </div>
              )}

              {/* Speaker Diarization */}
              {analysis.metadata.enable_speaker_diarization !== undefined && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Users className="w-4 h-4 text-teal-400" />
                    <span className="text-sm text-white/60">Diarization</span>
                  </div>
                  <p className={`font-semibold ${analysis.metadata.enable_speaker_diarization ? 'text-green-300' : 'text-red-300'}`}>
                    {analysis.metadata.enable_speaker_diarization ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              )}

              {/* Language Identification */}
              {analysis.metadata.enable_language_identification !== undefined && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Languages className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm text-white/60">Lang ID</span>
                  </div>
                  <p className={`font-semibold ${analysis.metadata.enable_language_identification ? 'text-green-300' : 'text-red-300'}`}>
                    {analysis.metadata.enable_language_identification ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              )}

              {/* Created At */}
              {analysis.metadata.created_at && (
                <div className="bg-white/5 rounded-lg p-4 border border-white/10 md:col-span-2">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-white/60">Created At</span>
                  </div>
                  <p className="text-white font-medium">
                    {new Date(analysis.metadata.created_at).toLocaleString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sales Team Quick Actions - NEW */}
        <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 backdrop-blur-md rounded-2xl shadow-lg border border-green-500/30 p-6">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
            <Zap className="w-6 h-6 text-green-400" />
            <span>Sales Team Quick Actions</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2">
              <Phone className="w-5 h-5" />
              <span>Schedule Call</span>
            </button>
            
            <button className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Book Site Visit</span>
            </button>
            
            <button className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2">
              <MessageCircle className="w-5 h-5" />
              <span>Send Follow-up</span>
            </button>
          </div>

          <div className="mt-4 p-4 bg-yellow-900/30 border border-yellow-500/40 rounded-xl">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-6 h-6 text-yellow-300 flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-yellow-200 font-semibold mb-1">Pro Tip</h4>
                <p className="text-yellow-100 text-sm">
                  {(() => {
                    const grade = analysis.lead_grade || analysis.lead_assessment?.lead_grade || analysis.executive_summary?.lead_grade;
                    if (grade === 'A') return 'This is a hot lead! Contact within 1 hour and schedule a site visit ASAP.';
                    if (grade === 'B') return 'Strong lead. Follow up within 24 hours with detailed project information.';
                    if (grade === 'C') return 'Good potential. Add to nurture campaign and follow up within 2-3 days.';
                    if (grade === 'D') return 'Requires qualification. Send informational email and monitor response.';
                    return 'Review lead details carefully before allocating resources.';
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Test Mode Banner */}
          {isTestMode && (
            <div className="mb-6 p-4 bg-yellow-900/30 border-2 border-yellow-500/50 rounded-xl">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-400" />
                <div>
                  <h3 className="text-yellow-300 font-semibold">Test Mode Active</h3>
                  <p className="text-yellow-200 text-sm">
                    Using sample data for demonstration. No actual API calls will be made.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-500 rounded-2xl mb-4">
              <Home className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Real Estate Deep Analysis
            </h1>
            <p className="text-xl text-purple-100">
              AI-powered analysis of real estate sales conversations
            </p>
          </div>

          {/* Configuration */}
          <div className="mb-6">
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span>Configuration</span>
            </button>

            {showConfig && (
              <div className="mt-4 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Transcription API URL
                    </label>
                    <input
                      type="text"
                      value={config.transcribeUrl}
                      onChange={(e) => handleConfigChange('transcribeUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Analysis API URL
                    </label>
                    <input
                      type="text"
                      value={config.analyzeUrl}
                      onChange={(e) => handleConfigChange('analyzeUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Config Path
                    </label>
                    <input
                      type="text"
                      value={config.configPath}
                      onChange={(e) => handleConfigChange('configPath', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Language Hints
                    </label>
                    <input
                      type="text"
                      value={config.languageHints}
                      onChange={(e) => handleConfigChange('languageHints', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="flex items-center space-x-2 text-white">
                    <input
                      type="checkbox"
                      checked={config.enableSpeakerDiarization}
                      onChange={(e) => handleConfigChange('enableSpeakerDiarization', e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <span>Enable Speaker Diarization</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Error messages */}
          {(transcriptionError || analysisError) && (
            <div className="mb-6 space-y-2">
              {transcriptionError && (
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <span className="text-red-300">{transcriptionError}</span>
                </div>
              )}
              {analysisError && (
                <div className="p-4 bg-orange-900/20 border border-orange-500/30 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-orange-400" />
                  <span className="text-orange-300">{analysisError}</span>
                </div>
              )}
            </div>
          )}

          {/* Main content */}
          {renderFileUpload()}
          {renderTranscription()}
          {renderAnalysis()}

          {/* Instructions */}
          <div className="mt-6 p-6 bg-blue-900/20 border border-blue-500/30 rounded-xl">
            <h3 className="text-lg font-medium text-blue-300 mb-4 flex items-center space-x-2">
              <Phone className="w-5 h-5" />
              <span>How to use Real Estate Deep Analysis:</span>
            </h3>
            <ul className="text-sm text-blue-200 space-y-2">
              <li>• <strong>Test Mode:</strong> Add <code className="bg-blue-800/40 px-2 py-0.5 rounded">?test=true</code> to the URL to try with sample data</li>
              <li>• Upload an MP3 file of a real estate sales call recording</li>
              <li>• Listen to the audio using the built-in player (optional)</li>
              <li>• Click "Transcribe Audio" to get the conversation text</li>
              <li>• Review the transcription with speaker diarization if enabled</li>
              <li>• Click "Analyze Conversation" to get deep AI insights</li>
              <li>• View detailed analysis including property details, customer intent, and more</li>
              <li>• Configure API endpoints in settings if needed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealEstateAnalysisPage;
